(ns frontend.view.components.clipboard
  (:require
   [clojure.string :as string]
   [common.localization :as loc]
   [frontend.mobile-utils :as mobile-utils]
   [frontend.state.current-session :as session]))

(def text-area-id "clipboard-textarea")
(def canvas-id "clipboard-canvas")

(defn copy-to
  [value]
  (when value
    (let [dom-node (.getElementById js/document text-area-id)]
      (.focus dom-node)               ;; focus 0 size text area
      (set! (.-value dom-node) value) ;; fill text to be copied to text area
      (if (and (session/is-mobile?) (session/ios?))
        (do                                                 ;; IOS specific
          (let [range (.createRange js/document)
                _ (.selectNodeContents range dom-node)
                selection (.getSelection js/window)
                _ (.removeAllRanges selection)
                _ (.addRange selection range)]
            (.setSelectionRange dom-node 0 999999))
          (mobile-utils/hide-keyboard))
        (do
          (.select dom-node)                                ;; all other browsers
          (let [res (.execCommand js/document "copy")]      ;; copy to clip board
            (set! (.-value dom-node) "")                    ;; clean textarea
            (reset! session/clipboard nil)                  ;; clean
            res))))))

(defn- option-label-for
  [options value]
  (some->> options
           (some (fn [opt]
                   (when (= (:value opt) value) opt)))
           :label))

(defn field-display-string
  "Resolve a labelled-input value to a human-readable string for copying.
   Handles multi-value (vec/seq), ref-single via options, and raw scalars."
  [value options]
  (cond
    (or (vector? value) (seq? value) (set? value))
    (->> value
         (keep (fn [v] (or (option-label-for options v) (str v))))
         (string/join ", "))

    (and (seq options) (some? value))
    (or (option-label-for options value) (str value))

    (some? value)
    (str value)))

(defn copy-tool
  "Builds a :tools entry for labelled-input/with-actions that copies the
   field's display value to the clipboard. Returns nil (i.e. no tool) when
   there's nothing to copy."
  [value options]
  (let [display (field-display-string value options)]
    (when (seq display)
      [{:icon     :tool-item/copy
        :tooltip  (loc/tr :copy)
        :on-click (fn [_] (copy-to display))}])))

(defn copy-as-tsv
  [data]
  (->> data
       (map (fn [r-data]
              (string/join "\t" r-data)))
       (string/join "\r\n")
       (copy-to)))

(defn copy-as-image
  [img-data]
  (.write js/navigator.clipboard
          [(js/ClipboardItem. #js {"image/png" img-data})]))


(defn createObjectURL
  [blob]
  (if-let [webkit-url (aget js/window "webkitURL")]
    (.call (aget webkit-url "createObjectURL") webkit-url blob)
    (when-let [url (aget js/window "URL")]
      (.call (aget url "createObjectURL") url blob))))


(defn on-image-loaded
  [e]
  (let [img (.-target e)
        width (.-width img)
        height (.-height img)
        canvas (.getElementById js/document canvas-id)
        ctx (.call (aget canvas "getContext") canvas "2d")]
    (aset canvas "width" width)
    (aset canvas "height" height)
    (.call (aget ctx "drawImage") ctx img 0 0)
    (-> (aget canvas "toDataURL")
        (.call canvas "image/png")
        (js/fetch)
        (.then (fn [resp]
                 (-> (.call (aget resp "blob") resp)
                     (.then (fn [blob]
                              (copy-as-image blob)
                              ))
                     (.catch (fn [err]
                               (js/console.log "Failed to load png blob : " err))))
                 (aset img "onload" nil)))
        (.catch (fn [err]
                  (js/console.log "Failed to render image to canvas " err))))))

(defn svg-as-png
  [svg-string]
  (let [parser (js/DOMParser.)
        svg-node (.call (aget parser "parseFromString")
                        parser
                        svg-string
                        "image/svg+xml")
        svg-elem (aget svg-node "documentElement")
        serializer (js/XMLSerializer.)
        svg-str (.call (aget serializer "serializeToString") serializer svg-elem)
        img (js/Image.)
        svg-blob (js/Blob. [svg-str] #js {"type" "image/svg+xml;charset=utf-8"})
        url (createObjectURL svg-blob)]
    (aset img "onload" on-image-loaded)
    (aset img "src" url)))

(defn view
  [value]
  [:<>
   [:textarea
    {:id                                text-area-id
     :class-name                        "zap-clipboard"
     :content-editable                  true
     :suppress-content-editable-warning true
     :read-only                         false
     :on-change                         (fn [e])
     :value                             (or value "")}]
   [:canvas
    {:id     canvas-id
     :width  "2000"
     :height "1000"
     :style  {:display  "none"
              :position "absolute"}}]
   [:div
    {:id "png-container"}]
   [:div
    {:id    "highcharts"
     :style {:display :none}}]])
